import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, Check, Image as ImageIcon, Loader2, ExternalLink, AlertCircle, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { ProductImageCategory } from '../../types/knowledge';
import productImageService, { ExtractedImage, ProductImage } from '../../services/api/productImageService';
import useProductImageImport from '../../hooks/useProductImageImport';
import { TaskySelect } from '../tasky';

export interface SelectedExtractedImage {
  url: string;
  alt: string;
  category: ProductImageCategory;
  title?: string;
  description?: string;
}

interface UrlImageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  productId?: number;
  onComplete: (savedImages: ProductImage[]) => void;
  onSkip: () => void;
}

type CategoryFilter = ProductImageCategory | 'all';

const CATEGORY_CONFIG: Record<ProductImageCategory, { label: string; color: string }> = {
  brand_logo: { label: 'ブランドロゴ', color: '#3B82F6' },
  product_image: { label: '商品/サービス画像', color: '#22C55E' },
  material: { label: 'イメージ素材', color: '#A855F7' },
};

// 旧 UrlImageSelectionModal.scss をここへ内製化した。値は「自ファイルの記述」ではなく
// CSS バンドル順で実際に勝っていた実効値（Modal.scss / ProductImageSelectionModal.scss /
// StyleSelectModal.scss / output.css からの上書きを含む）。
const MODAL_HEADER =
  'flex shrink-0 items-start justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5 max-[640px]:p-4';
const MODAL_CONTENT =
  'flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 ' +
  '[scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] ' +
  '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] ' +
  '[&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] ' +
  '[&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] max-[640px]:p-4';
const CLOSE_BUTTON =
  'flex h-9 w-9 items-center justify-center rounded-lg border-none bg-transparent text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-800';
// 白系ゴーストボタン（スキップ導線 3 箇所で共通）
const SKIP_GHOST_BUTTON =
  'rounded-md border border-gray-300 bg-white px-4 py-2 text-[13px] font-semibold text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50';

// .image-card 系。Tailwind は className の順で勝敗が決まらないため、
// 状態ごとに background / border-color / cursor が重複しない排他的な完全文字列にする。
const CARD_BASE = 'overflow-hidden rounded-xl border-2 transition-all';
const CARD_DEFAULT = `${CARD_BASE} cursor-pointer border-gray-200 bg-white hover:border-blue-500 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]`;
const CARD_SELECTED = `${CARD_BASE} cursor-pointer border-blue-500 bg-white shadow-[0_0_0_3px_rgba(59,130,246,0.2)]`;
const CARD_FAILED = `${CARD_BASE} cursor-pointer border-[#fca5a5] bg-red-50 hover:border-[#ef4444]`;
const CARD_UNSELECTED = `${CARD_BASE} cursor-pointer border-gray-200 bg-white opacity-70 hover:border-blue-500 hover:opacity-100 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]`;
const CARD_SAVING = `${CARD_BASE} cursor-pointer border-amber-300 bg-white opacity-70`;
const CARD_SAVED = `${CARD_BASE} cursor-default border-green-300 bg-white opacity-[0.85] hover:border-green-500 hover:opacity-100`;

const IMAGE_WRAPPER = 'group/wrap relative aspect-[4/3] w-full cursor-pointer overflow-hidden bg-[#f5f5f5]';
const IMAGE_WRAPPER_STATIC = 'relative aspect-[4/3] w-full cursor-default overflow-hidden bg-[#f5f5f5]';
const IMAGE_EL = 'h-full w-full object-contain transition-transform group-hover/wrap:scale-105';
const IMAGE_EL_STATIC = 'h-full w-full object-contain';
const IMAGE_INFO = 'p-3';
// カテゴリ選択は TaskySelect（DS）に寄せた。旧 --select-color によるカテゴリ色の枠線は
// DS のトークン外だったため落としている（カテゴリ自体は選択肢のラベルで読める）。
const CATEGORY_SELECT = 'mb-1.5 h-8 text-sm';
const CHECKBOX_OVERLAY_BASE =
  'absolute left-auto right-1.5 top-1.5 flex h-[19px] w-[19px] items-center justify-center rounded-md border-none transition-all';
const CHECKBOX_OVERLAY = `${CHECKBOX_OVERLAY_BASE} bg-white/90`;
const CHECKBOX_OVERLAY_CHECKED = `${CHECKBOX_OVERLAY_BASE} bg-primary text-white`;

const STATUS_OVERLAY_BASE = 'absolute inset-0 flex items-center justify-center transition-all duration-300';
const GROUP_HEADER_BASE = 'm-0 mb-3 flex items-center gap-2 text-sm font-semibold [&>svg]:shrink-0';
const IMAGES_GRID =
  'grid gap-4 grid-cols-[repeat(auto-fill,minmax(180px,1fr))] max-[640px]:gap-3 max-[640px]:grid-cols-[repeat(auto-fill,minmax(150px,1fr))]';

const CATEGORY_TAB_BASE =
  'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-[5px] text-[11px] font-medium transition-all';
const CATEGORY_TAB = `${CATEGORY_TAB_BASE} border-gray-200 bg-gray-100 text-gray-500 hover:bg-gray-200`;
/** API 側の images[].title の検証上限（max:255）に合わせる */
const IMAGE_TITLE_MAX_LENGTH = 255;

const CATEGORY_TAB_ACTIVE = `${CATEGORY_TAB_BASE} border-[var(--category-color,#3b82f6)] bg-[var(--category-color,#3b82f6)] text-white`;
const CATEGORY_COUNT = 'rounded-full bg-gray-200 px-[5px] py-px text-[10px] text-gray-500 opacity-[0.62]';
const CATEGORY_COUNT_ACTIVE = 'rounded-full bg-white/20 px-[5px] py-px text-[10px] text-white opacity-[0.62]';

const UrlImageSelectionModal: React.FC<UrlImageSelectionModalProps> = ({
  isOpen,
  onClose,
  url,
  productId,
  onComplete,
  onSkip,
}) => {
  const [extracted, setExtractedImages] = useState<ExtractedImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Map<string, SelectedExtractedImage>>(new Map());
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');
  const [extractionError, setError] = useState<string | null>(null);
  const imageImport = useProductImageImport({ isOpen, productId, url, onComplete });
  const { saving, saveProgress, uploadErrors, imageSaveStatus, importImages } = imageImport;
  const error = imageImport.error || extractionError;
  const extractionController = useRef<AbortController | null>(null);
  const importImagesRef = useRef(importImages);
  importImagesRef.current = importImages;
  // A resumed batch may contain URLs that the source page no longer exposes.
  const extractedImages = useMemo<ExtractedImage[]>(() => [...extracted, ...importImages
    .filter((image) => !extracted.some((entry) => entry.url === image.url))
    .map((image) => ({ url: image.url, alt: image.title || '', type: 'img' as const,
      width: null, height: null, suggested_category: image.category }))], [extracted, importImages]);
  // 抽出にかかっている経過秒数（無言スピナーで「固まって見える」のを防ぐ）
  const [elapsedSec, setElapsedSec] = useState(0);

  // 抽出中だけ経過秒数をカウント。loadingが終わったらリセット。
  useEffect(() => {
    if (!loading) {
      setElapsedSec(0);
      return;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [loading]);

  const extractImages = useCallback(async () => {
    extractionController.current?.abort();
    const controller = new AbortController();
    extractionController.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await productImageService.extractImagesFromUrl(url, controller.signal);
      if (controller.signal.aborted) return;
      setExtractedImages(response.data);
      const initialSelection = new Map<string, SelectedExtractedImage>();
      if (importImagesRef.current.length) {
        importImagesRef.current.forEach((img) => initialSelection.set(img.url, { ...img, alt: img.title || '' }));
      } else {
        response.data.forEach((img) => initialSelection.set(img.url, {
          url: img.url, alt: img.alt, category: img.suggested_category,
        }));
      }
      setSelectedImages(initialSelection);
    } catch (err: any) {
      if (!controller.signal.aborted) setError(err?.message || '画像の抽出に失敗しました');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    setExtractedImages([]);
    setSelectedImages(new Map());
    setActiveCategory('all');
    setError(null);
    setLoading(false);
    if (isOpen && url) void extractImages();
    return () => extractionController.current?.abort();
  }, [isOpen, url, productId, extractImages]);

  useEffect(() => {
    if (importImages.length) {
      setSelectedImages(new Map(importImages.map((img) => [img.url, { ...img, alt: img.title || '' }])));
    }
  }, [importImages]);

  const handleImageToggle = useCallback((image: ExtractedImage) => {
    if (importImages.length) return;
    setSelectedImages((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(image.url)) {
        newMap.delete(image.url);
      } else {
        newMap.set(image.url, {
          url: image.url,
          alt: image.alt,
          category: image.suggested_category,
        });
      }
      return newMap;
    });
  }, [importImages.length]);

  const handleCategoryChange = useCallback((imageUrl: string, category: ProductImageCategory) => {
    if (importImages.length) return;
    setSelectedImages((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(imageUrl);
      if (existing) {
        newMap.set(imageUrl, { ...existing, category });
      }
      return newMap;
    });
  }, [importImages.length]);

  const handleSelectAll = useCallback(() => {
    const newMap = new Map<string, SelectedExtractedImage>();
    const filtered = activeCategory === 'all'
      ? extractedImages
      : extractedImages.filter((img) => img.suggested_category === activeCategory);

    filtered.forEach((img) => {
      newMap.set(img.url, {
        url: img.url,
        alt: img.alt,
        category: img.suggested_category,
      });
    });

    // Keep existing selections from other categories
    if (activeCategory !== 'all') {
      selectedImages.forEach((value, key) => {
        const img = extractedImages.find((e) => e.url === key);
        if (img && img.suggested_category !== activeCategory) {
          newMap.set(key, value);
        }
      });
    }

    setSelectedImages(newMap);
  }, [extractedImages, activeCategory, selectedImages]);

  const handleDeselectAll = useCallback(() => {
    if (activeCategory === 'all') {
      setSelectedImages(new Map());
    } else {
      setSelectedImages((prev) => {
        const newMap = new Map(prev);
        extractedImages
          .filter((img) => img.suggested_category === activeCategory)
          .forEach((img) => newMap.delete(img.url));
        return newMap;
      });
    }
  }, [extractedImages, activeCategory]);

  /*
   * 抽出した alt を title として保存する。
   *
   * 選択中の項目は {url, alt, category} で作られ、保存を組み立てる
   * useProductImageImport は {url, category, title, description} を取り出すので、
   * ここで title を補わないと alt は送られない（product_images.title が
   * 全件空だった原因）。alt は画面に出ており Jev の役割判定の入力でもあるのに、
   * 保存の瞬間だけ捨てていた。
   *
   * ・再取り込み経路は既に title を持っているので、そちらを優先して上書きしない。
   * ・API は title を max:255 で検証する。長い alt をそのまま送ると
   *   バッチ全体が 422 で落ちるので、ここで切り詰める。
   */
  const handleSave = () => imageImport.save(
    Array.from(selectedImages.values()).map((image) => ({
      ...image,
      title: image.title ?? (image.alt ? image.alt.slice(0, IMAGE_TITLE_MAX_LENGTH) : undefined),
    })),
  );
  const handleRetry = (image: ExtractedImage) => imageImport.retry(image.url);

  const filteredImages = activeCategory === 'all'
    ? extractedImages
    : extractedImages.filter((img) => img.suggested_category === activeCategory);

  // Group images by their save status for display
  const getGroupedImages = useCallback(() => {
    const failed: ExtractedImage[] = [];
    const unselected: ExtractedImage[] = [];
    const pending: ExtractedImage[] = [];
    const saving: ExtractedImage[] = [];
    const saved: ExtractedImage[] = [];

    filteredImages.forEach((img) => {
      const saveState = imageSaveStatus.get(img.url);

      if (saveState?.status === 'failed') {
        failed.push(img);
      } else if (saveState?.status === 'saved') {
        saved.push(img);
      } else if (saveState?.status === 'saving') {
        saving.push(img);
      } else if (selectedImages.has(img.url)) {
        pending.push(img);
      } else {
        unselected.push(img);
      }
    });

    return { failed, unselected, pending, saving, saved };
  }, [filteredImages, imageSaveStatus, selectedImages]);

  const groupedImages = getGroupedImages();
  const hasSaveStatus = imageSaveStatus.size > 0;

  const getCategoryCount = (category: ProductImageCategory) => {
    return extractedImages.filter((img) => img.suggested_category === category).length;
  };

  const isAllSelected = () => {
    if (filteredImages.length === 0) return false;
    return filteredImages.every((img) => selectedImages.has(img.url));
  };

  if (!isOpen) return null;

  const hasErrors = uploadErrors.length > 0;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[rgba(29,25,39,0.38)] p-6 max-[640px]:p-3"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[1000px] flex-col overflow-hidden rounded-[18px] bg-white shadow-[0_24px_60px_rgba(29,25,39,0.22)] max-[640px]:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={MODAL_HEADER}>
          <div className="flex flex-col items-start gap-1 max-[640px]:gap-2">
            <h2 className="m-0 flex items-center gap-2 text-[18px] font-semibold text-gray-800">画像を選択</h2>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-[200px] items-center gap-1.5 rounded-md bg-[#F6F5F0] px-2.5 py-1 text-xs text-gray-500 no-underline transition-all hover:bg-[#EFEEE9] hover:text-primary max-[640px]:max-w-[150px]"
              title={url}
            >
              <ExternalLink size={14} />
              <span className="truncate">{new URL(url).hostname}</span>
            </a>
          </div>
          <button className={CLOSE_BUTTON} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className={MODAL_CONTENT}>
          {error && (
            <div className="mb-4 flex items-center justify-between gap-2 rounded-lg border border-[#fecaca] bg-destructive/10 p-6 text-center text-sm text-destructive [grid-column:1/-1] [&_svg]:shrink-0 [&_svg]:text-[#dc2626]">
              <AlertCircle size={16} />
              <span className="flex-1 text-sm text-[#dc2626]">{error}</span>
              <button
                className="rounded-md border-none bg-[#dc2626] px-3 py-1.5 text-[13px] text-white transition-colors hover:bg-[#b91c1c]"
                onClick={imageImport.error ? handleSave : extractImages}
                disabled={saving}
              >
                再試行
              </button>
              <button
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-[13px] text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50"
                onClick={onSkip}
              >
                スキップして手動で進む
              </button>
            </div>
          )}

          {loading && importImages.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-gray-500">
              <Loader2 className="mb-4 animate-spin text-blue-500" size={32} />
              <p className="m-0 text-sm">URLから画像を抽出中...（{elapsedSec}秒）</p>
              <span className="max-w-[400px] truncate text-xs text-gray-400">{url}</span>
              {elapsedSec >= 20 && (
                <p className="mt-3 max-w-[420px] text-center text-[13px] text-[#b45309]">
                  時間がかかっています。スキップして後から画像を追加することもできます。
                </p>
              )}
              <button className={`mt-4 ${SKIP_GHOST_BUTTON}`} onClick={onSkip}>
                スキップして手動で進む
              </button>
            </div>
          ) : extractedImages.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-sm text-gray-400 [&>svg]:mb-4 [&>svg]:opacity-50">
              <ImageIcon size={48} />
              <p className="m-0 text-sm">抽出できる画像が見つかりませんでした</p>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  className="rounded-md border-none bg-primary px-4 py-2 text-[13px] text-white transition-colors hover:bg-[#4F5BB5]"
                  onClick={extractImages}
                >
                  再試行
                </button>
                <button className={SKIP_GHOST_BUTTON} onClick={onSkip}>
                  スキップして手動で進む
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {/* Category Tabs */}
              <div className="flex flex-wrap items-center justify-between gap-3 max-[640px]:flex-col max-[640px]:items-stretch">
                <div className="mb-5 flex flex-wrap gap-2 max-[640px]:flex-nowrap max-[640px]:overflow-x-auto max-[640px]:pb-1 max-[640px]:[&::-webkit-scrollbar]:hidden">
                  <button
                    className={activeCategory === 'all' ? CATEGORY_TAB_ACTIVE : CATEGORY_TAB}
                    onClick={() => setActiveCategory('all')}
                  >
                    すべて
                    <span className={activeCategory === 'all' ? CATEGORY_COUNT_ACTIVE : CATEGORY_COUNT}>
                      {extractedImages.length}
                    </span>
                  </button>
                  {(Object.keys(CATEGORY_CONFIG) as ProductImageCategory[]).map((category) => (
                    <button
                      key={category}
                      className={activeCategory === category ? CATEGORY_TAB_ACTIVE : CATEGORY_TAB}
                      onClick={() => setActiveCategory(category)}
                      style={{
                        '--category-color': CATEGORY_CONFIG[category].color,
                      } as React.CSSProperties}
                    >
                      {CATEGORY_CONFIG[category].label}
                      <span className={activeCategory === category ? CATEGORY_COUNT_ACTIVE : CATEGORY_COUNT}>
                        {getCategoryCount(category)}
                      </span>
                    </button>
                  ))}
                </div>
                <div>
                  {isAllSelected() ? (
                    <button
                      className="rounded-md border-none bg-transparent px-3 py-1.5 text-[11.5px] font-bold text-primary transition-all hover:bg-[#F6F5F0] hover:text-[#4F5BB5]"
                      disabled={importImages.length > 0}
                      onClick={handleDeselectAll}
                    >
                      すべて解除
                    </button>
                  ) : (
                    <button
                      className="rounded-md border-none bg-transparent px-3 py-1.5 text-[11.5px] font-bold text-primary transition-all hover:bg-[#F6F5F0] hover:text-[#4F5BB5]"
                      disabled={importImages.length > 0}
                      onClick={handleSelectAll}
                    >
                      すべて選択
                    </button>
                  )}
                </div>
              </div>

              {/* Images Grid - Grouped by status after save attempt */}
              {hasSaveStatus ? (
                <div className="flex flex-col gap-6">
                  {/* Failed Images */}
                  {groupedImages.failed.length > 0 && (
                    <div>
                      <h4 className={`${GROUP_HEADER_BASE} text-[#dc2626]`}>
                        <AlertTriangle size={16} />
                        保存に失敗した画像 ({groupedImages.failed.length}件)
                      </h4>
                      <div className={IMAGES_GRID}>
                        {groupedImages.failed.map((image) => {
                          const saveState = imageSaveStatus.get(image.url);
                          return (
                            <div key={image.url} className={CARD_FAILED}>
                              <div className={IMAGE_WRAPPER}>
                                <img
                                  src={image.url}
                                  alt={image.alt || 'Extracted image'}
                                  loading="lazy"
                                  className={IMAGE_EL}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                <div className={`${STATUS_OVERLAY_BASE} bg-[rgba(220,38,38,0.15)] [&>svg]:text-[#dc2626]`}>
                                  <AlertCircle size={20} />
                                </div>
                              </div>
                              <div className={IMAGE_INFO}>
                                <p className="m-0 mb-2 text-[11px] leading-[1.4] text-[#dc2626]">
                                  {saveState?.error || '保存に失敗しました'}
                                </p>
                                <button
                                  className="inline-flex items-center gap-1.5 rounded-md border-none bg-[#dc2626] px-3 py-1.5 text-xs font-medium text-white transition-all enabled:hover:bg-[#b91c1c] disabled:cursor-not-allowed disabled:opacity-70"
                                  onClick={() => handleRetry(image)}
                                  disabled={saving}
                                >
                                  {saveState?.status === 'saving' ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <RefreshCw size={14} />
                                  )}
                                  再試行
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Unselected Images */}
                  {groupedImages.unselected.length > 0 && (
                    <div>
                      <h4 className={`${GROUP_HEADER_BASE} text-gray-500`}>
                        選択していない画像 ({groupedImages.unselected.length}件)
                      </h4>
                      <div className={IMAGES_GRID}>
                        {groupedImages.unselected.map((image) => {
                          const selectedData = selectedImages.get(image.url);
                          return (
                            <div
                              key={image.url}
                              className={CARD_UNSELECTED}
                              onClick={() => handleImageToggle(image)}
                            >
                              <div className={IMAGE_WRAPPER}>
                                <img
                                  src={image.url}
                                  alt={image.alt || 'Extracted image'}
                                  loading="lazy"
                                  className={IMAGE_EL}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                <div className={CHECKBOX_OVERLAY} />
                              </div>
                              <div className={IMAGE_INFO}>
                                <TaskySelect
                                  className={CATEGORY_SELECT}
                                  disabled={importImages.length > 0}
                                  value={selectedData?.category || image.suggested_category}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const newCategory = e.target.value as ProductImageCategory;
                                    setSelectedImages((prev) => {
                                      const newMap = new Map(prev);
                                      newMap.set(image.url, {
                                        url: image.url,
                                        alt: image.alt,
                                        category: newCategory,
                                      });
                                      return newMap;
                                    });
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(Object.keys(CATEGORY_CONFIG) as ProductImageCategory[]).map((cat) => (
                                    <option key={cat} value={cat}>
                                      {CATEGORY_CONFIG[cat].label}
                                    </option>
                                  ))}
                                </TaskySelect>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Pending (selected but not yet saved) Images */}
                  {groupedImages.pending.length > 0 && (
                    <div>
                      <h4 className={`${GROUP_HEADER_BASE} text-blue-500`}>
                        選択中の画像 ({groupedImages.pending.length}件)
                      </h4>
                      <div className={IMAGES_GRID}>
                        {groupedImages.pending.map((image) => {
                          const selectedData = selectedImages.get(image.url);
                          return (
                            <div
                              key={image.url}
                              className={CARD_SELECTED}
                              onClick={() => handleImageToggle(image)}
                            >
                              <div className={IMAGE_WRAPPER}>
                                <img
                                  src={image.url}
                                  alt={image.alt || 'Extracted image'}
                                  loading="lazy"
                                  className={IMAGE_EL}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                <div className={CHECKBOX_OVERLAY_CHECKED}>
                                  <Check size={16} />
                                </div>
                              </div>
                              <div className={IMAGE_INFO}>
                                <TaskySelect
                                  className={CATEGORY_SELECT}
                                  disabled={importImages.length > 0}
                                  value={selectedData?.category || image.suggested_category}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleCategoryChange(image.url, e.target.value as ProductImageCategory);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(Object.keys(CATEGORY_CONFIG) as ProductImageCategory[]).map((cat) => (
                                    <option key={cat} value={cat}>
                                      {CATEGORY_CONFIG[cat].label}
                                    </option>
                                  ))}
                                </TaskySelect>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Saving Images */}
                  {groupedImages.saving.length > 0 && (
                    <div>
                      <h4 className={`${GROUP_HEADER_BASE} text-amber-500`}>
                        <Loader2 size={16} className="animate-spin" />
                        保存中 ({groupedImages.saving.length}件)
                      </h4>
                      <div className={IMAGES_GRID}>
                        {groupedImages.saving.map((image) => (
                          <div key={image.url} className={CARD_SAVING}>
                            <div className={IMAGE_WRAPPER}>
                              <img
                                src={image.url}
                                alt={image.alt || 'Extracted image'}
                                loading="lazy"
                                className={IMAGE_EL}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                              <div className={`${STATUS_OVERLAY_BASE} bg-amber-500/20 [&>svg]:text-amber-500`}>
                                <Loader2 size={24} className="animate-spin" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Saved Images */}
                  {groupedImages.saved.length > 0 && (
                    <div>
                      <h4 className={`${GROUP_HEADER_BASE} text-green-500`}>
                        <CheckCircle2 size={16} />
                        保存済み ({groupedImages.saved.length}件)
                      </h4>
                      <div className={IMAGES_GRID}>
                        {groupedImages.saved.map((image) => (
                          <div key={image.url} className={CARD_SAVED}>
                            <div className={IMAGE_WRAPPER_STATIC}>
                              <img
                                src={image.url}
                                alt={image.alt || 'Extracted image'}
                                loading="lazy"
                                className={IMAGE_EL_STATIC}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                              <div className={`${STATUS_OVERLAY_BASE} bg-green-500/20 [&>svg]:text-green-500`}>
                                <CheckCircle2 size={20} />
                              </div>
                              <span className="absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-green-500 px-3 py-1 text-[11px] font-semibold text-white">
                                保存済み
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Normal grid before save attempt */
                <div className={IMAGES_GRID}>
                  {filteredImages.map((image) => {
                    const isSelected = selectedImages.has(image.url);
                    const selectedData = selectedImages.get(image.url);
                    return (
                      <div
                        key={image.url}
                        className={isSelected ? CARD_SELECTED : CARD_DEFAULT}
                      >
                        <div
                          className={IMAGE_WRAPPER}
                          onClick={() => handleImageToggle(image)}
                        >
                          <img
                            src={image.url}
                            alt={image.alt || 'Extracted image'}
                            loading="lazy"
                            className={IMAGE_EL}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                          <div className={isSelected ? CHECKBOX_OVERLAY_CHECKED : CHECKBOX_OVERLAY}>
                            {isSelected && <Check size={16} />}
                          </div>
                          {image.type === 'og' && (
                            <span className="absolute right-2 top-2 rounded px-2 py-[3px] text-[10px] font-semibold uppercase bg-amber-500 text-white">
                              OGP
                            </span>
                          )}
                          {image.type === 'twitter' && (
                            <span className="absolute right-2 top-2 rounded px-2 py-[3px] text-[10px] font-semibold uppercase bg-[#1da1f2] text-white">
                              Twitter
                            </span>
                          )}
                        </div>
                        <div className={IMAGE_INFO}>
                          <TaskySelect
                            className={CATEGORY_SELECT}
                            value={selectedData?.category || image.suggested_category}
                            onChange={(e) => {
                              const newCategory = e.target.value as ProductImageCategory;
                              if (!isSelected) {
                                setSelectedImages((prev) => {
                                  const newMap = new Map(prev);
                                  newMap.set(image.url, {
                                    url: image.url,
                                    alt: image.alt,
                                    category: newCategory,
                                  });
                                  return newMap;
                                });
                              } else {
                                handleCategoryChange(image.url, newCategory);
                              }
                            }}
                          >
                            {(Object.keys(CATEGORY_CONFIG) as ProductImageCategory[]).map((cat) => (
                              <option key={cat} value={cat}>
                                {CATEGORY_CONFIG[cat].label}
                              </option>
                            ))}
                          </TaskySelect>
                          {image.alt && (
                            <p className="m-0 mb-1 truncate text-xs text-gray-500" title={image.alt}>
                              {image.alt}
                            </p>
                          )}
                          {image.width && image.height && (
                            <span className="text-[11px] text-gray-400">
                              {image.width} × {image.height}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={
            hasErrors
              ? 'flex shrink-0 flex-col items-stretch justify-between gap-4 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[640px]:gap-3'
              : 'flex shrink-0 items-center justify-between gap-4 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[640px]:flex-col max-[640px]:items-stretch max-[640px]:gap-3'
          }
        >
          {/* Upload Errors Display */}
          {hasErrors && (
            <div className="w-full rounded-lg border border-[#ffc107] bg-[#fef3cd] p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-[#856404] [&>svg]:shrink-0 [&>svg]:text-[#d97706]">
                <AlertTriangle size={16} />
                <span>{uploadErrors.length}件の画像のアップロードに失敗しました</span>
              </div>
              <ul className="m-0 max-h-[120px] list-none overflow-y-auto p-0">
                {uploadErrors.map((err, idx) => (
                  <li
                    key={idx}
                    className="flex flex-col gap-0.5 border-b border-b-[rgba(133,100,4,0.1)] py-1.5 text-xs last:border-b-0"
                  >
                    <span className="break-all text-gray-500" title={err.url}>
                      {err.url.length > 50 ? `${err.url.substring(0, 50)}...` : err.url}
                    </span>
                    <span className="font-medium text-[#dc2626]">{err.error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex w-full items-center justify-between gap-4">
            <div className="flex-1">
              {saveProgress ? (
                <div className="flex items-center gap-2 text-sm text-[#44C2A4]">
                  {saving ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>{saveProgress.total}件中{saveProgress.saved}件を保存（残り{saveProgress.processing}件を処理中）</span>
                    </>
                  ) : hasErrors ? (
                    <>
                      <AlertTriangle size={16} className="text-[#d97706]" />
                      <span>{saveProgress.saved}件を保存（{uploadErrors.length}件失敗）</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} className="text-[#44C2A4]" />
                      <span>{saveProgress.saved}件の画像を保存しました</span>
                    </>
                  )}
                </div>
              ) : (
                <span className="text-sm text-gray-500">
                  選択中: {selectedImages.size}件
                </span>
              )}
            </div>
            <div className="flex shrink-0 gap-3 max-[640px]:w-full max-[640px]:justify-end max-[640px]:[&>button]:flex-1">
              <button
                className="rounded-lg border border-[#E8E6E0] bg-white px-5 py-2.5 text-sm font-bold text-gray-500 transition-all enabled:hover:bg-[#F6F5F0] enabled:hover:text-[#1D1927] disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onSkip}
                disabled={saving}
              >
                スキップ
              </button>
              {hasErrors && saveProgress && saveProgress.saved > 0 ? (
                <button
                  className="inline-flex items-center gap-2 rounded-lg border-none bg-[#44C2A4] px-6 py-2.5 text-sm font-bold text-white transition-all enabled:hover:bg-[#2E9B7E] disabled:cursor-not-allowed disabled:bg-gray-400"
                  disabled={saving || !!imageImport.error}
                  onClick={imageImport.complete}
                >
                  {saveProgress.saved}件の画像で続行
                </button>
              ) : (
                <button
                  className="inline-flex items-center gap-2 rounded-lg border-none bg-primary px-6 py-2.5 text-sm font-bold text-white transition-all enabled:hover:bg-[#4F5BB5] disabled:cursor-not-allowed disabled:bg-gray-400"
                  onClick={handleSave}
                  disabled={(selectedImages.size === 0 && importImages.length === 0) || saving || !productId}
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      保存中...
                    </>
                  ) : (
                    imageImport.error && importImages.length > 0 ? '保存状況を再確認' :
                      uploadErrors.length > 0 ? `失敗した画像を再試行 (${uploadErrors.length})` :
                        `選択した画像を保存 (${selectedImages.size})`
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrlImageSelectionModal;
